import { FC, useRef } from 'react';
import classNames from 'classnames';
import Signature from '../../svgs/Signature';
import {
  ProgramActivityDataFragment,
  SignatureUser,
  UserType,
} from '../../../generated/graphql';
import TooltipLight from '../TooltipLight';
import { getColorForSignatureUserType } from '../../lib/multiSignature';
import { formatSignatureDate } from '../../lib/signatureUtils';
import AnimatedStatusIndicator from './AnimatedStatusIndicator';

interface SignatureSummaryProps {
  programActivity: ProgramActivityDataFragment;
  className?: string;
  authedProviderUserId?: string;
  context?: 'mail' | 'activities';
  userType?: UserType;
}

const SignatureSummary: FC<SignatureSummaryProps> = ({
  programActivity,
  className,
  authedProviderUserId,
  context = 'mail',
  userType = UserType.ProviderUser,
}) => {
  // Hooks must be declared unconditionally before any early returns
  const iconRef = useRef<HTMLSpanElement>(null);

  const signatureUsers = [
    ...(programActivity.programAssessment?.programAssessmentSignatureUsers ||
      []),
  ].sort((a, b) => a.orderIndex - b.orderIndex);
  const currentSigner =
    programActivity.programAssessment?.currentSigner || null;

  if (!programActivity.programAssessment || signatureUsers.length === 0) {
    return null;
  }

  const signedCount = signatureUsers.filter((u) => Boolean(u.signedAt)).length;
  const totalCount = signatureUsers.length;
  const allSigned = signedCount === totalCount;

  const currentSignerName =
    currentSigner?.providerUser?.name ||
    currentSigner?.patientName ||
    'Unknown';

  // For provider users: check if authedProviderUserId matches currentSigner's providerUser
  // For patient users: check if currentSigner has no providerUser (meaning patient is current signer)
  const isAuthedCurrentSigner = Boolean(
    userType === UserType.Patient
      ? !currentSigner?.providerUser?.id
      : authedProviderUserId &&
          currentSigner?.providerUser?.id &&
          authedProviderUserId === currentSigner.providerUser.id,
  );

  const summaryRow = (
    <div
      className={classNames(
        'flex items-center text-small-caption text-neutral-125',
        className,
      )}
    >
      {/* Anchor element for tooltip alignment */}
      <span ref={iconRef} className="inline-flex">
        <Signature className="text-neutral-500 mr-6 h-3 w-3" />
      </span>
      {allSigned ? (
        <>
          <div className="ml-1 mr-1 flex shrink-0 items-center justify-center">
            <AnimatedStatusIndicator
              isSigned={true}
              isCurrentSigner={false}
              size="md"
            />
          </div>
          <span className="mr-1">Signed</span>
          <span>
            ({signedCount}/{totalCount})
          </span>
        </>
      ) : (
        <>
          <div className="ml-1 mr-2 flex shrink-0 items-center justify-center">
            <AnimatedStatusIndicator
              isSigned={false}
              isCurrentSigner={true}
              size="md"
            />
          </div>
          <span className="mr-1">
            Waiting for{' '}
            {isAuthedCurrentSigner
              ? 'you to sign'
              : `${currentSignerName} to sign`}
          </span>
          <span>
            ({signedCount}/{totalCount})
          </span>
        </>
      )}
    </div>
  );

  // Tooltip content: detailed signers list (light background, black text, below trigger)
  const tooltipContent = (
    <>
      {signatureUsers.map((u) => {
        const isSigned = Boolean(u.signedAt);
        const signerType =
          u.assessmentSignatureUser?.signatureUserType ||
          SignatureUser.PatientUser;
        const colorScheme = getColorForSignatureUserType(signerType);
        const fullName = u.providerUser?.name || u.patientName || 'Unknown';
        const isCurrentSigner = currentSigner?.id === u.id;
        const signedAt = u.signedAt;

        return (
          <div
            key={u.id}
            className="flex items-center gap-2 whitespace-nowrap py-1"
          >
            {/* Status indicator */}
            <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
              <AnimatedStatusIndicator
                isSigned={isSigned}
                isCurrentSigner={isCurrentSigner}
                size="md"
              />
            </div>
            {/* Name badge */}
            <div
              className={classNames(
                'text-neutral-700 inline-flex rounded-full px-2 py-1 text-small-caption',
                colorScheme.backgroundColor,
              )}
            >
              <span className="font-normal">{fullName}</span>
            </div>
            {/* Status text */}
            <span className="text-small-caption text-neutral-125">
              {isSigned
                ? formatSignatureDate(signedAt)
                : isCurrentSigner
                ? 'Awaiting signature'
                : 'Not signed yet'}
            </span>
          </div>
        );
      })}
    </>
  );

  return (
    <TooltipLight
      content={tooltipContent}
      placement="bottom"
      offsetX={0}
      anchorRef={iconRef}
    >
      {summaryRow}
    </TooltipLight>
  );
};

export default SignatureSummary;
